import { AccountTableView } from "@bitwarden/web-vault/app/models/types/account.types";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { SyncStatusView } from "@bitwarden/web-vault/app/models/view/sync-status/sync-status.view";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { SyncStatusEnum } from "@bitwarden/web-vault/app/models/types/general-types";
import { SyncStatusStoreModel } from "@bitwarden/web-vault/app/models/store/sync-status.store.model";
import { PreferenceView } from "@bitwarden/web-vault/app/models/view/preference/preference.view";
import {
  COUNTRIES,
  GLOSS_CREDITS_ACCOUNT_NAME,
  GLOSS_VAULT,
} from "@bitwarden/web-vault/app/models/constants/global.constants";
import { GLOSS_VAULT_ACCOUNT_ID_SUFFIX } from "@bitwarden/web-vault/app/shared/utils/helper.institution/gloss-vault-institution.helper";

export function getAccountTableViews(
  accountViews: AccountView[],
  syncStatusViews: SyncStatusView[],
  institutionViews: InstitutionView[],
  balances: Map<string, number>,
  preference: PreferenceView,
): AccountTableView[] {
  return accountViews?.map((accountView) => {
    const accountClone = accountView.clone();
    const institutionView = institutionViews?.find(
      (view) => view.id === accountView.institutionLink.institutionId,
    );
    const country = institutionView
      ? (COUNTRIES.find((country) => country.code === institutionView?.swift?.countryCode)?.name ??
        "")
      : "";

    // Extract max interest rate from institutionAccountType
    let maxInterestRate = NaN;
    if (accountView.c_institutionAccountType?.interestRates?.length > 0) {
      maxInterestRate = accountView.c_institutionAccountType.interestRates.reduce(
        (max, current) => (current.rate > max ? current.rate : max),
        accountView.c_institutionAccountType.interestRates[0].rate,
      );
    }

    // Get account product name from institutionAccountType
    // Check if the product name already includes the institution name to avoid duplication
    const rawProductName = accountView.c_institutionAccountType?.name || "";
    const institutionName = institutionView?.name || "";
    let accountProductName = "";

    if (accountView.name === GLOSS_CREDITS_ACCOUNT_NAME) {
      accountProductName = GLOSS_VAULT;
    } else if (rawProductName && institutionName) {
      // Check if product name already starts with institution name (case-insensitive)
      const productNameLower = rawProductName.toLowerCase();
      const institutionNameLower = institutionName.toLowerCase();

      if (productNameLower.startsWith(institutionNameLower)) {
        // Product name already includes institution name, use as is
        accountProductName = rawProductName;
      } else {
        // Product name doesn't include institution, prepend it
        accountProductName = `${institutionName} ${rawProductName}`;
      }
    } else if (rawProductName) {
      accountProductName = rawProductName;
    }

    return {
      original: accountClone,
      country,
      institution: institutionView?.name,
      full_logo_url: institutionView?.full_logo_url,
      square_logo_url: institutionView?.square_logo_url,
      id: accountView.id,
      // institutionCode: institutionView?.bic?.bankCode?.toLowerCase(),
      name: accountView.displayName,
      interestRate: maxInterestRate,
      balance: balances?.get(accountView.id),
      symbol: preference.coreCurrency,
      statusPoint: getAccountStatusView(accountView, syncStatusViews),
      type: accountView.type,
      currency: accountView.currency,
      accountProductName: accountProductName,
    } as AccountTableView;
  });
}

/** @Sinan once all is ready remove the mockSyncStatus and return from the service */
export function getAccountStatusView(
  accountView: AccountView,
  syncStatusViews: SyncStatusView[],
): SyncStatusView {
  /* For gloss credits account, we need to return a mock sync status for now. It will be removed once gloss credits is turned to fiskil type sync flow  */
  // Identify Gloss Credits account by institution account id suffix where possible,
  // fallback to name check for older records.
  const instAccId = accountView.institutionLink?.institutionAccountId;
  if (
    (instAccId && instAccId.endsWith(GLOSS_VAULT_ACCOUNT_ID_SUFFIX)) ||
    accountView.name === GLOSS_CREDITS_ACCOUNT_NAME
  ) {
    const mockSyncStatusModel = {
      key: SyncStatusEnum.synced,
      acId: accountView.id,
      dc: new Date().toISOString(),
    } as SyncStatusStoreModel;

    return new SyncStatusView(mockSyncStatusModel);
  }

  if (syncStatusViews.length) {
    const ordered = syncStatusViews.sort(
      (a, b) => b.dateCreated.getTime() - a.dateCreated.getTime(),
    );

    return ordered.find((view) => view.accountId === accountView.id);
  } else {
    const mockSyncStatusModel = {
      key: SyncStatusEnum.synced,
      acId: accountView.id,
      dc: new Date().toISOString(),
    } as SyncStatusStoreModel;

    return new SyncStatusView(mockSyncStatusModel);
  }
}
